import React, { useState, useRef } from "react";
import { Part } from "../../Services/PartService";
import { ManualPart } from "./types";
import { CustomDropdown } from "../CustomDropdown";
import api from "../../Services/api";

// Define an interface for the error objects in the response
interface ImportError {
    partNumber: string;
    error: string;
}

// Define an interface for the upload response
interface UploadResponse {
    success: boolean;
    items: ManualPart[];
    errors?: ImportError[];
    message?: string;
}

interface InvoicePartsSectionProps {
    parts: Part[];
    manualParts: ManualPart[];
    currency: string;
    onAddPart: () => void;
    onRemovePart: (id: string) => void;
    onUpdatePart: (id: string, field: keyof ManualPart, value: any) => void;
    onUpdatePartFull: (id: string, updatedPart: Partial<ManualPart>) => void;
    onCurrencyChange: (currency: string) => void;
    onAddMultipleParts: (newParts: ManualPart[]) => void;
}

export const InvoicePartsSection: React.FC<InvoicePartsSectionProps> = ({
    parts,
    manualParts,
    currency,
    onAddPart,
    onRemovePart,
    onUpdatePart,
    onUpdatePartFull,
    onCurrencyChange,
    onAddMultipleParts
}) => {
    const [selectedPartId, setSelectedPartId] = useState<string>("");
    const [showBulkImport, setShowBulkImport] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>("");
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handlePartSelect = (partId: string) => {
        const selectedPart = parts.find(p => p._id === partId);
        if (selectedPart) {
            const newManualPart: ManualPart = {
                id: Date.now().toString(),
                partNumber: selectedPart.partNumber,
                description: selectedPart.description || "",
                hsCode: selectedPart.hsCode || "",
                countryOfOrigin: selectedPart.countryOfOrigin || "",
                quantity: 1,
                unitPrice: selectedPart.unitValue || 0,
                usmca: selectedPart.usmca || "No",
                usmcaOriginCriteria: selectedPart.usmcaOriginCriteria || "",
                usmcaQualificationMethod: selectedPart.usmcaQualificationMethod || "",
            };

            // Always add a new part when selecting from inventory
            onAddMultipleParts([newManualPart]);
            setSelectedPartId("");
        }
    };


    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check if it's a CSV file
        if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
            setUploadError("Please upload a CSV file");
            return;
        }

        setIsUploading(true);
        setUploadError("");

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post<UploadResponse>('/cci/items/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success && response.data.items.length > 0) {
                onAddMultipleParts(response.data.items);
                setShowBulkImport(false);
            } else if (response.data.errors && response.data.errors.length > 0) {
                setUploadError(`Import had errors: ${response.data.errors.map((e: ImportError) => `${e.partNumber}: ${e.error}`).join(', ')}`);
            } else {
                setUploadError("No valid items found in the CSV file");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            setUploadError("Failed to upload file. Please try again.");
        } finally {
            setIsUploading(false);
            // Reset the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const downloadTemplate = async () => {
        try {
            const response = await api.get('/cci/template/download', {
                responseType: 'blob'
            });

            // Create a blob URL for the file
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'invoice-items-template.csv');
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading template:", error);
            setUploadError("Failed to download template");
        }
    };

    const calculateTotal = () => {
        return manualParts.reduce((sum, part) => sum + (part.quantity * part.unitPrice), 0);
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Invoice Items</h3>
                <div className="flex space-x-2">
                    <select
                        value={currency}
                        onChange={(e) => onCurrencyChange(e.target.value)}
                        className="border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    >
                        <option value="USD">USD ($)</option>
                        <option value="CAD">CAD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                    </select>
                    <button
                        type="button"
                        onClick={downloadTemplate}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                        Download Template
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowBulkImport(!showBulkImport)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        {showBulkImport ? "Hide Bulk Import" : "Bulk Import"}
                    </button>
                </div>
            </div>

            {showBulkImport && (
                <div className="mb-4 p-3 border border-gray-300 rounded-md dark:border-gray-600">
                    <h4 className="text-sm font-medium mb-2">Bulk Import Parts from CSV</h4>
                    <p className="text-xs text-gray-500 mb-2">
                        Upload a CSV file with your invoice items. Use the template for the correct format.
                    </p>
                    <div className="flex items-center space-x-2">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept=".csv"
                            className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                        {isUploading && <span className="text-sm text-gray-500">Uploading...</span>}
                    </div>
                    {uploadError && (
                        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                    )}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Part Number
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Description
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                HS Code
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Origin
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Qty
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Unit Price
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Total
                            </th>
                            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                        {manualParts.map((part) => (
                            <tr key={part.id}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    <input
                                        type="text"
                                        value={part.partNumber}
                                        onChange={(e) => onUpdatePart(part.id, 'partNumber', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                        required
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    <input
                                        type="text"
                                        value={part.description || ''}
                                        onChange={(e) => onUpdatePart(part.id, 'description', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    <input
                                        type="text"
                                        value={part.hsCode || ''}
                                        onChange={(e) => onUpdatePart(part.id, 'hsCode', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    <input
                                        type="text"
                                        value={part.countryOfOrigin || ''}
                                        onChange={(e) => onUpdatePart(part.id, 'countryOfOrigin', e.target.value)}
                                        className="w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    <input
                                        type="number"
                                        value={part.quantity}
                                        onChange={(e) => onUpdatePart(part.id, 'quantity', parseInt(e.target.value) || 0)}
                                        min="1"
                                        className="w-16 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    <input
                                        type="number"
                                        value={part.unitPrice}
                                        onChange={(e) => onUpdatePart(part.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                        min="0"
                                        step="0.01"
                                        className="w-24 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                    />
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                    {(part.quantity * part.unitPrice).toFixed(2)} {currency}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                                    <button
                                        type="button"
                                        onClick={() => onRemovePart(part.id)}
                                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={6} className="px-3 py-2 text-right text-sm font-medium">
                                Total:
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                                {calculateTotal().toFixed(2)} {currency}
                            </td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="mt-4 flex justify-between items-center">
                <button
                    type="button"
                    onClick={onAddPart}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Add New Item
                </button>

                {parts.length > 0 && (
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">Quick add from inventory:</span>
                        <div className="w-64">
                            <CustomDropdown
                                options={parts.map(p => ({
                                    id: p._id || "", // Add the empty string fallback here
                                    primaryText: p.partNumber,
                                    secondaryText: p.description || ""
                                }))}
                                value={selectedPartId}
                                onChange={handlePartSelect}
                                placeholder="Select a part"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

